
import { M3uMedia, DEFAULT_MEDIA_DURATION } from '../../lib/m3u';
import Loading from '../common/Loading';
import { MimeType } from '../../lib/m3u/m3u-model';
import { useCategoryParams } from '../../hooks/useCategoryParams';
import styles from './categories.module.css';
import tooltipStyles from '../common/Tooltip/tooltip.module.css';
import React from 'react';
import { useCategoryStreams, CategoryStream } from '../../hooks/useCategoryStreams';
import { SelectedChannelContext } from '../../context/channel/SelectedChannelContext';
import { AccountContext } from "../../context/account/AccountContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBookmark } from '@fortawesome/free-solid-svg-icons';
import { playlistShortlistService } from '../../services/playlist/PlaylistShortlistService';

interface CategoryStreamsProps {
    onClose?: () => void;
}

const CategoryStreams: React.FC<CategoryStreamsProps> = ({ onClose }) => {
    const { data: accountData } = React.useContext(AccountContext);
    const { selectChannel } = React.useContext(SelectedChannelContext);
    const [params] = useCategoryParams();

    const { data: streams, isLoading, error } = useCategoryStreams({
        baseUrl: accountData?.url || '',
        username: accountData?.username || '',
        password: accountData?.password || '',
        categoryId: params.categoryId,
        streamType: params.streamAction
    });

    if (!accountData) {
        return <div className={styles.error}>Ingen kontoinformasjon tilgjengelig</div>;
    }

    if (!params.categoryId) {
        return <div className={styles.empty}>Velg en kategori for å se kanaler</div>;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <div className={styles.error}>Feil ved lasting av kanaler: {error.message}</div>;
    }

    if (!streams || streams.length === 0) {
        return <div className={styles.empty}>Ingen kanaler funnet i denne kategorien</div>;
    }

    const handleStreamClick = (stream: CategoryStream) => {

        const streamUrl = buildStreamUrl(stream);

        const m3uMedia: M3uMedia = {
            name: stream.name,
            location: streamUrl,
            mimeType: getMimeTypeFromExtension(stream.container_extension, params.streamAction),
            duration: stream.duration ?? DEFAULT_MEDIA_DURATION,
            attributes: {
                'tvg-id': stream.stream_id?.toString() || '',
                'tvg-name': stream.name,
                'tvg-logo': stream.stream_icon || '',
                'group-title': stream.category_name || ''
            }
        };

        selectChannel(m3uMedia);
    };

    const addToShortlist = (e: React.MouseEvent, stream: CategoryStream) => {
        e.stopPropagation(); // Prevent triggering the stream click

        const streamUrl = buildStreamUrl(stream);

        const m3uMedia: M3uMedia = {
            name: stream.name,
            location: streamUrl,
            mimeType: getMimeTypeFromExtension(stream.container_extension, params.streamAction),
            duration: stream.duration ?? DEFAULT_MEDIA_DURATION,
            attributes: {
                'tvg-id': stream.stream_id?.toString() || '',
                'tvg-name': stream.name,
                'tvg-logo': stream.stream_icon || '',
                'group-title': stream.category_name || ''
            }
        };

        playlistShortlistService.add(m3uMedia);
    };

    const buildStreamUrl = (stream: CategoryStream): string => {
        if (!accountData) return '';
        const { url, username, password } = accountData;
        const baseUrl = url.replace(/\/$/, '');

        if (params.streamAction === 'get_live_streams') {
            // Live stream URL format: http://domain:port/username/password/stream_id
            return `${baseUrl}/${username}/${password}/${stream.stream_id}`;
        } else if (params.streamAction === 'get_vod_streams') {
            // VOD stream URL format: http://domain:port/movie/username/password/stream_id.ext
            const extension = stream.container_extension || 'mp4';
            return `${baseUrl}/movie/${username}/${password}/${stream.stream_id}.${extension}`;
        } else if (params.streamAction === 'get_series') {
            // Series URL - you might need to handle episodes differently
            // For now, return a placeholder or the series info URL
            return `${baseUrl}/series/${username}/${password}/${stream.series_id}`;
        }

        return '';
    };

    const getMimeTypeFromExtension = (extension: string | undefined, streamType: string): MimeType => {
        // If no extension provided, use default based on stream type
        if (!extension) {
            return streamType === 'get_series' ? MimeType.MP4 : MimeType.M2TS;
        }

        // Map container extension to MimeType
        const ext = extension.toLowerCase();
        switch (ext) {
            case 'mp4':
            case 'm4v':
                return MimeType.MP4;
            case 'mkv':
                return MimeType.MP4;
            case 'avi':
                return MimeType.AVI;
            case 'm3u8':
            case 'ts':
            case 'm2ts':
                return MimeType.M2TS;
            default:
                // Default to M2TS for live streams, MP4 for VOD
                return streamType === 'get_vod_streams' ? MimeType.MP4 : MimeType.M2TS;
        }
    };

    return (
        <div className={styles.categoriesContainer}>
            <div className={styles.streamsHeader}>
                <h2 className={styles.title}>Kanaler ({streams.length})</h2>
                {onClose && (
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        title="Lukk kanalliste"
                        aria-label="Lukk kanalliste"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
            </div>
            <ul className={styles.streamsList}>
                {streams.map((stream) => (
                    <li
                        key={stream.stream_id}
                        className={styles.streamItem}
                        onClick={() => handleStreamClick(stream)}
                    >
                        <div className={styles.streamActions}>
                            <div className={styles.tooltipContainer} title="Legg til shortlist">
                                <FontAwesomeIcon
                                    icon={faBookmark}
                                    onClick={(e) => addToShortlist(e, stream)}
                                    className={styles.addIcon}
                                />
                            </div>
                        </div>
                        <div className={styles.streamInfo}>
                            <span
                                className={`${styles.streamName} ${tooltipStyles.tooltipTruncated}`}
                                data-tooltip={stream.name}
                            >
                                {stream.name}
                            </span>
                        </div>

                    </li>
                ))}
            </ul>
        </div>
    );
};

export default CategoryStreams;